import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EmployeeDocument } from '@app/models/documents/employee-document.model';
import { SignatureRequest } from '@app/models/documents/signature-request.model';
import { AuthService, NotifyService } from '@app/services';
import { HelloSignEmbedService } from '@app/services/hello-sign-embed.service';
import { SignEventPayload } from '@app/types/hello-sign-namespace.type';

@Component({
    templateUrl: './employee-document-sign.template.html',
    styleUrls: ['./employee-document-sign.style.scss'],
    providers: [HelloSignEmbedService],
})
export class EmployeeDocumentSignView implements OnInit, OnDestroy {
    @ViewChild('helloSignContainer', { static: true }) helloSignContainer!: ElementRef<HTMLDivElement>;
    employeeDocument: EmployeeDocument = new EmployeeDocument();
    signatureRequest: SignatureRequest = new SignatureRequest();
    isLoading = true;
    isSigned = false;
    noAssignment = false;

    constructor(
        private auth: AuthService,
        private route: ActivatedRoute,
        private notify: NotifyService,
        private router: Router,
        private helloSignEmbed: HelloSignEmbedService
    ) {}

    ngOnDestroy(): void {
        this.helloSignEmbed.destroy();
    }

    async ngOnInit(): Promise<void> {
        this.employeeDocument = this.route.snapshot.data.models.get('employeeDocument');
        const source = this.employeeDocument.source as SignatureRequest;
        try {
            this.signatureRequest = await SignatureRequest.param('signingTemplate', source.signingTemplateId).find(
                source.id
            );
        } catch (e) {
            this.handleError(e as Error);
            return;
        }

        this.isLoading = false;
        this.isSigned = this.signatureRequest.isComplete();

        if (this.isSigned || !this.signatureRequest.signingUrl) {
            return;
        }

        this.helloSignEmbed.onSignEventOnce(async (data: SignEventPayload) => {
            try {
                this.isLoading = true;
                await this.auth.checkThenReauth();
                this.isLoading = true;
                if (data.signatureId) {
                    this.signatureRequest.completedAt = new Date();
                    await this.signatureRequest.save();
                    this.notify.success('forms.setup-documents.documentSignedMessage');
                }
                this.goToDocuments();
            } catch (e) {
                this.notify.error('document.savingDocumentFailed');
                this.goBack();
            }
        });
        this.helloSignEmbed.onEndOnce('close', () => {
            this.goBack();
        });
        this.helloSignEmbed.onEndOnce('cancel', () => {
            this.goBack();
        });

        try {
            await this.helloSignEmbed.open(this.signatureRequest.signingUrl, this.helloSignContainer, true);
        } catch (e) {
            this.noAssignment = true;
            this.notify.error(e as Error);
        }
        this.isLoading = false;
    }

    goBack(): void {
        if (this.auth.isAdmin()) {
            this.router.navigate(['documents-v2']);
        } else {
            this.router.navigate(['profile', 'documents-v2']);
        }
    }

    goToDocuments(): void {
        if (this.employeeDocument.isOwnedBy(this.auth.employee)) {
            if (this.auth.isAdmin()) {
                this.router.navigate(['documents-v2']);
            } else {
                this.router.navigate(['profile', 'documents-v2']);
            }
            return;
        }

        // @TODO set URL for viewing someone else's documents
        this.router.navigate(['/']);
    }

    handleError(error: Error): void {
        this.isLoading = false;
        this.notify.error(error.message);
    }
}
